import React, { useState } from 'react';
import { CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FileDownload, TableChart } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { EXCEL_EXPORT_FEATURE_FLAG, ExportFormat } from './exportTypes';
import { useApiResponseHandlerContext } from '../apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../apiNotifications/ApiResponseHandlerContext';

type ExportButtonProps = {
  onExport: (format: ExportFormat) => void | Promise<void>;
  variant?: 'icon' | 'button';
  /** Tooltip text for the icon variant, button text for the button variant. */
  label?: string;
  disabled?: boolean;
  /** Externally-driven loading (e.g. the reports fetch), OR-ed with this component's own. */
  loading?: boolean;
};

/**
 * The single place the Excel export flag is read. With the flag off this renders exactly what was
 * here before and downloads CSV on click; with it on, the same trigger opens a format menu.
 */
export const ExportButton = ({ onExport, variant = 'icon', label, disabled, loading }: ExportButtonProps) => {
  const { t } = useTranslation();
  const { showResponseNotification } = useApiResponseHandlerContext();
  const excelExportEnabled = useFeatureFlagEnabled(EXCEL_EXPORT_FEATURE_FLAG);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // The first xlsx click downloads ~270KB of JS before it produces anything, so this needs a spinner.
  const busy = isExporting || !!loading;

  const runExport = async (format: ExportFormat) => {
    setAnchorEl(null);
    setIsExporting(true);
    try {
      await onExport(format);
    } catch (error) {
      console.error('Export failed:', error);
      showResponseNotification(t('export.failed'), ApiResponseHandlerType.ERROR);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (excelExportEnabled) {
      setAnchorEl(event.currentTarget);
      return;
    }
    void runExport(ExportFormat.CSV);
  };

  const trigger =
    variant === 'button' ? (
      <LoadingButton loading={busy} variant="contained" color="primary" onClick={handleClick} disabled={disabled}>
        {label ?? t('export.download')}
      </LoadingButton>
    ) : (
      <Tooltip title={label ?? t('export.download')} arrow>
        <span>
          <IconButton onClick={handleClick} disabled={disabled} aria-label={label ?? t('export.download')}>
            {busy ? <CircularProgress size={20} /> : <FileDownload />}
          </IconButton>
        </span>
      </Tooltip>
    );

  return (
    <>
      {trigger}
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => runExport(ExportFormat.CSV)}>
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('export.csv')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runExport(ExportFormat.XLSX)}>
          <ListItemIcon>
            <TableChart fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('export.excel')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
